import { useEffect, useMemo, useState } from 'react';
import { Alert, Input, Pagination, Table } from 'antd';

function getByPath(obj, path) {
  if (!path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

/**
 * Shared table: client-side search across `searchKeys` (supports "user.email"),
 * plus column-defined filters & sorters (Ant Design).
 */
export default function BaseTable({
  columns,
  dataSource,
  loading = false,
  rowKey = 'id',
  searchKeys = [],
  searchPlaceholder = 'Search…',
  emptyText = 'No data.',
  error = null,
  tableProps = {}
}) {
  const {
    pagination: userPagination,
    scroll: userScroll,
    onChange: userOnChange,
    bordered: userBordered,
    ...restTableProps
  } = tableProps;

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return dataSource;
    return dataSource.filter((row) =>
      searchKeys.some((path) => {
        const v = getByPath(row, path);
        return String(v ?? '')
          .toLowerCase()
          .includes(q);
      })
    );
  }, [dataSource, searchKeys, searchText]);

  const totalRows = filteredData.length;

  useEffect(() => {
    setPage(1);
  }, [searchText, dataSource]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalRows / pageSize) || 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalRows, pageSize, page]);

  const handleTableChange = (nextPagination, filters, sorter, extra) => {
    if (userPagination !== false && nextPagination) {
      const nextPage = nextPagination.current ?? 1;
      const nextSize = nextPagination.pageSize;
      setPage(nextPage);
      if (nextSize != null) {
        setPageSize(nextSize);
      }
    }
    userOnChange?.(nextPagination, filters, sorter, extra);
  };

  const toolbarPaginationProps = useMemo(() => {
    if (userPagination === false) {
      return null;
    }
    const base = {
      current: page,
      pageSize,
      total: totalRows,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50],
      showQuickJumper: false,
      showTotal: (total, range) =>
        total === 0 ? '0 items' : `${range[0]}–${range[1]} of ${total}`,
      onChange: (nextPage, nextSize) => {
        setPage(nextPage);
        if (nextSize != null) {
          setPageSize(nextSize);
        }
      }
    };
    if (userPagination && typeof userPagination === 'object') {
      const { onChange: userPaginationOnChange, ...userRest } = userPagination;
      return {
        ...base,
        ...userRest,
        current: page,
        pageSize,
        total: totalRows,
        showQuickJumper: false,
        onChange: (nextPage, nextSize) => {
          base.onChange(nextPage, nextSize);
          userPaginationOnChange?.(nextPage, nextSize);
        }
      };
    }
    return base;
  }, [userPagination, page, pageSize, totalRows]);

  const tablePaginationProps = useMemo(() => {
    if (!toolbarPaginationProps) {
      return false;
    }
    const { className: userClass, ...rest } = toolbarPaginationProps;
    return {
      ...rest,
      className: ['care-table-pagination--hidden', userClass].filter(Boolean).join(' ')
    };
  }, [toolbarPaginationProps]);

  const scrollMerged = { x: 'max-content', ...(userScroll || {}) };

  const showToolbar = searchKeys.length > 0 || toolbarPaginationProps != null;

  return (
    <div className="care-data-panel">
      {error ? (
        <Alert
          type="error"
          showIcon
          message={error.message || 'Request failed'}
        />
      ) : null}
      {showToolbar ? (
        <div
          className={
            searchKeys.length > 0
              ? 'care-base-table-toolbar care-base-table-toolbar--with-search'
              : 'care-base-table-toolbar'
          }
        >
          {searchKeys.length > 0 ? (
            <Input.Search
              allowClear
              className="care-search care-base-table-search"
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          ) : null}
          {toolbarPaginationProps ? (
            <Pagination
              {...toolbarPaginationProps}
              className={['care-base-table-pagination', toolbarPaginationProps.className]
                .filter(Boolean)
                .join(' ')}
            />
          ) : null}
        </div>
      ) : null}
      <Table
        className="care-antd-table"
        rowKey={rowKey}
        loading={loading}
        columns={columns}
        dataSource={filteredData}
        pagination={tablePaginationProps}
        onChange={handleTableChange}
        scroll={scrollMerged}
        locale={{ emptyText }}
        bordered={userBordered !== false}
        {...restTableProps}
      />
    </div>
  );
}
